import * as React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { atom, useAtomValue } from 'jotai';
import { Resolution, Margin, Options, usePDF } from 'react-to-pdf';
import { QrCodeIcon } from '@heroicons/react/24/outline';
import { useAnalytics } from '@restworld/utils-common';

import { ButtonV2, Spinner, Title } from '@restworld/ui-ds';
import { useAuth } from '@restworld/feature-authentication';

import { LayoutWrapper } from '../../../../../../layouts';
import RestaurantShowCaseQRA4 from './restaurant-show-case-qr-a4';
import ShowcaseInfoCard from '../showcase-info-card';
import ShowcaseItem from '../showcase-item';
import { useRestaurantNotPublicFlag } from '../../../../hooks';
import RestaurantShowCaseFullPageQRA4 from './restaurant-showcase-full-page-qr';
import RestaurantShowCaseQR from './restaurant-show-case-qr';

export const isPDFReadyAtom = atom<boolean>(false);
export const composePDFOptions = (fileName: string, format?: string) =>
  ({
    filename: `${fileName}.pdf`,
    method: 'save',
    resolution: Resolution.MEDIUM, // specify 300 dpi
    page: {
      margin: 2.5,
      format: format || 'A4',
      orientation: 'portrait',
    },
    canvas: {
      mimeType: 'image/jpeg',
      qualityRatio: 1, // reduce quality to reduce size
    },
    overrides: {
      pdf: {
        compress: true,
      },
      canvas: {
        useCORS: false,
      },
    },
  } as Options);
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IRestaurantQRCodeDownloadProps {
  fetchRestaurantInformationQR?: string;
  variant?: 'showcase_info_card' | 'showcase_item';
}
const RestaurantQRCodeDownload: React.FunctionComponent<IRestaurantQRCodeDownloadProps> = ({
  fetchRestaurantInformationQR,
  variant,
}) => {
  const { t } = useTranslation('restaurant-details-page');
  const { trackPHEvent } = useAnalytics();
  const { user } = useAuth();
  const { query, isFallback } = useRouter();
  const restaurantName = query.slug as string;
  const isPDFReady = useAtomValue(isPDFReadyAtom);
  const isShowcaseOffline = useRestaurantNotPublicFlag();

  const pdfOptions = composePDFOptions(restaurantName);
  const { toPDF, targetRef } = usePDF(pdfOptions);

  // React.useEffect(() => {
  //   if (isPDFReady && !variant) toPDF();
  // }, [isPDFReady, toPDF, variant]);

  const toPDFCb = React.useCallback(() => {
    trackPHEvent('employer_click_qr_code', { email: user?.email, restaurant_name: restaurantName });
    toPDF();
  }, [restaurantName, toPDF, trackPHEvent, user?.email]);

  return (
    <>
      {variant &&
        (variant === 'showcase_info_card' ? (
          <ShowcaseInfoCard
            image="/utils-assets/qr-code-icon.svg"
            // title={t('tired_of_paper_cvs')}
            content={t('print_qr_code_and_hang_it')}
            button={{
              text: t('print_qr_code'),
              Icon: QrCodeIcon,
              onClick: toPDFCb,
              loading: !isPDFReady,
            }}
            isOffline={isShowcaseOffline}
          />
        ) : (
          <ShowcaseItem
            key={2}
            image="/utils-assets/person-with-qrcode.svg"
            content={t('attach_the_qr_on_the_door_and_start_receiving_applications')}
            ctaEl={
              <ButtonV2 StartIcon={QrCodeIcon} onClick={toPDFCb}>
                {t('print_qr_code')}
              </ButtonV2>
            }
          />
        ))}
      {variant ? (
        <div style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}>
          <RestaurantShowCaseQRA4 ref={targetRef} restaurantDetailsQR={fetchRestaurantInformationQR} />
        </div>
      ) : (
        <LayoutWrapper title={restaurantName}>
          {isFallback && (
            <Spinner withBackDrop color="primary" size="lg">
              <Title textColor="neutral-700">{isFallback ? t('loading') : t('loading_restaurants_qr_code')}...</Title>
            </Spinner>
          )}
          <RestaurantShowCaseQRA4 ref={targetRef} restaurantDetailsQR={fetchRestaurantInformationQR} />
        </LayoutWrapper>
      )}
    </>
  );
};

export default RestaurantQRCodeDownload;
export { RestaurantShowCaseFullPageQRA4, RestaurantShowCaseQRA4, RestaurantShowCaseQR };
